import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  InputAdornment,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { CertificateMechanicalValueCreate, MechanicalProperty } from '../types';

interface CertificateMechanicalValuesFormProps {
  mechanicalValues: CertificateMechanicalValueCreate[];
  onChange: (values: CertificateMechanicalValueCreate[]) => void;
  materialGradeMechanicalProperties: MechanicalProperty[];
  disabled?: boolean;
}

type ValidationColor = 'success' | 'error' | 'warning';

interface ValidationStatus {
  isValid: boolean;
  message: string;
  color: ValidationColor;
}

function formatRangeMessage(
  minValue: number | null | undefined,
  maxValue: number | null | undefined,
  unit: string | null | undefined
): string {
  const unitStr = unit || '';
  const hasMin = minValue !== null && minValue !== undefined;
  const hasMax = maxValue !== null && maxValue !== undefined;

  if (hasMin && hasMax) {
    return `(${minValue} - ${maxValue} ${unitStr})`;
  }
  if (hasMin) {
    return `(min. ${minValue} ${unitStr})`;
  }
  if (hasMax) {
    return `(max. ${maxValue} ${unitStr})`;
  }
  return '(kein Bereich definiert)';
}

function CertificateMechanicalValuesForm({
  mechanicalValues,
  onChange,
  materialGradeMechanicalProperties,
  disabled = false,
}: CertificateMechanicalValuesFormProps): React.ReactElement {
  const availableProperties = materialGradeMechanicalProperties.map((prop) => prop.property_name);

  function getValidationStatus(
    mechanicalValue: CertificateMechanicalValueCreate
  ): ValidationStatus {
    if (!mechanicalValue.property_name.trim()) {
      return {
        isValid: false,
        message: 'Eigenschaftsname erforderlich',
        color: 'error',
      };
    }

    const property = materialGradeMechanicalProperties.find(
      (prop) => prop.property_name.toLowerCase() === mechanicalValue.property_name.toLowerCase()
    );

    if (!property) {
      return {
        isValid: false,
        message: 'Eigenschaft nicht in Materialgüte definiert',
        color: 'warning',
      };
    }

    const { measured_value } = mechanicalValue;
    const { min_value, max_value, unit } = property;
    const rangeMessage = formatRangeMessage(min_value, max_value, unit);

    const hasMin = min_value !== null && min_value !== undefined;
    const hasMax = max_value !== null && max_value !== undefined;

    let isInRange = true;
    if (hasMin && measured_value < min_value!) {
      isInRange = false;
    }
    if (hasMax && measured_value > max_value!) {
      isInRange = false;
    }

    return {
      isValid: isInRange,
      message: isInRange ? `Im Bereich ${rangeMessage}` : `Außerhalb des Bereichs ${rangeMessage}`,
      color: isInRange ? 'success' : 'error',
    };
  }

  function getDuplicateErrors(): string[] {
    const propertyNames = mechanicalValues.map((v) => v.property_name.toLowerCase().trim());
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    for (const name of propertyNames) {
      if (name && seen.has(name)) {
        duplicates.add(name);
      }
      seen.add(name);
    }

    return duplicates.size > 0 ? ['Eigenschaften dürfen nicht doppelt vorkommen'] : [];
  }

  function handleMechanicalValueChange(
    index: number,
    field: keyof CertificateMechanicalValueCreate,
    value: string | number
  ): void {
    const newValues = [...mechanicalValues];

    if (field === 'property_name' || field === 'unit') {
      newValues[index][field] = value as string;

      if (field === 'property_name') {
        const property = materialGradeMechanicalProperties.find((p) => p.property_name === value);
        if (property?.unit) {
          newValues[index].unit = property.unit;
        }
      }
    } else {
      newValues[index][field] = Number(value);
    }

    onChange(newValues);
  }

  function addMechanicalValue(): void {
    onChange([...mechanicalValues, { property_name: '', measured_value: 0, unit: '' }]);
  }

  function removeMechanicalValue(index: number): void {
    onChange(mechanicalValues.filter((_, i) => i !== index));
  }

  function addAllRequiredProperties(): void {
    const existingProperties = mechanicalValues.map((v) => v.property_name.toLowerCase());
    const newValues = [...mechanicalValues];

    materialGradeMechanicalProperties.forEach((prop) => {
      if (!existingProperties.includes(prop.property_name.toLowerCase())) {
        newValues.push({
          property_name: prop.property_name,
          measured_value: 0,
          unit: prop.unit || '',
        });
      }
    });

    onChange(newValues);
  }

  const errors = getDuplicateErrors();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2">Gemessene mechanische Eigenschaften</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {materialGradeMechanicalProperties.length > 0 && (
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={addAllRequiredProperties}
              disabled={disabled}
            >
              Alle Eigenschaften hinzufügen
            </Button>
          )}
          <Button
            type="button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addMechanicalValue}
            disabled={disabled}
            size="small"
          >
            Eigenschaft hinzufügen
          </Button>
        </Box>
      </Box>

      {materialGradeMechanicalProperties.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {materialGradeMechanicalProperties.map((prop, index) => (
            <Chip
              key={index}
              label={`${prop.property_name} ${formatRangeMessage(prop.min_value, prop.max_value, prop.unit)}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {mechanicalValues.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Keine mechanischen Eigenschaften. „Eigenschaft hinzufügen“ oder alle Eigenschaften.
        </Typography>
      ) : (
        <Box>
          {mechanicalValues.map((mechanicalValue, index) => {
            const validationStatus = getValidationStatus(mechanicalValue);
            return (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr 110px 36px',
                    sm: 'minmax(140px,1.2fr) 130px minmax(120px,1.4fr) 36px',
                  },
                  gap: 1,
                  alignItems: 'center',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Autocomplete
                  value={mechanicalValue.property_name}
                  onChange={(_, newValue) =>
                    handleMechanicalValueChange(index, 'property_name', newValue || '')
                  }
                  onInputChange={(_, newInputValue) =>
                    handleMechanicalValueChange(index, 'property_name', newInputValue)
                  }
                  options={availableProperties}
                  freeSolo
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField {...params} label="Eigenschaft" required />
                  )}
                />
                <TextField
                  type="number"
                  label="Ist"
                  value={mechanicalValue.measured_value}
                  onChange={(e) =>
                    handleMechanicalValueChange(index, 'measured_value', e.target.value)
                  }
                  inputProps={{ step: 'any' }}
                  disabled={disabled}
                  InputProps={{
                    endAdornment: mechanicalValue.unit ? (
                      <InputAdornment position="end">{mechanicalValue.unit}</InputAdornment>
                    ) : null,
                  }}
                />
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  {mechanicalValue.property_name.trim() && (
                    <>
                      {validationStatus.isValid ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <ErrorIcon color={validationStatus.color} fontSize="small" />
                      )}
                      <Typography variant="caption" color={`${validationStatus.color}.main`} noWrap>
                        {validationStatus.message}
                      </Typography>
                    </>
                  )}
                </Box>
                <IconButton
                  onClick={() => removeMechanicalValue(index)}
                  disabled={disabled}
                  color="error"
                  size="small"
                  title="Eigenschaft entfernen"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default CertificateMechanicalValuesForm;
